import React, { useState } from "react";
import ThemeSelector from "./ThemeSelector";
import { okaidia } from "react-syntax-highlighter/dist/esm/styles/prism";
import SyntaxHighlighter from "react-syntax-highlighter";
import { irBlack } from "react-syntax-highlighter/dist/esm/styles/hljs";
import MarkdownParagraph from "../../components/MarkedDownParagraph";

function Task({
  user_id,
  topicFront,
  taskData,
  loading,
  error,
  onSelectTheme,
  hideThemeSelector,
  hideTopicName,
  onTaskDataUpdate,
  showComplexityButtons = true,
  course_name,
}) {
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState("");
  const [commentSent, setCommentSent] = useState(false);

  const [loadingEasier, setLoadingEasier] = useState(false);
  const [loadingMoreDifficult, setLoadingMoreDifficult] = useState(false);

  const toggleCommentBox = () => {
    setShowCommentBox((prev) => {
      if (prev) {
        setCommentSent(false);
      }
      return !prev;
    });
  };

  const handleSendComment = async () => {
    if (!taskData) return;

    const payload = {
      user_id: user_id,
      topic: taskData.topic,
      task_number: taskData.task_number,
      comment: comment,
      course_name: course_name,
    };

    try {
      const response = await fetch("api/tasks/comment", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const responseData = await response.json();

      setCommentSent(true);

      setComment("");
    } catch (error) {
      console.error("Не удалось отправить коммент:", error);
    }
  };

  const handleEasierClick = async () => {
    if (!taskData) return;

    setLoadingEasier(true);
    const payload = {
      topic: taskData.topic,
      task_number: taskData.task_number,
      direction_of_complexity: "easier",
      user_id: user_id,
      course_name: course_name,
    };

    try {
      const response = await fetch("api/tasks/level", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const newTaskData = await response.json();
      if (onTaskDataUpdate) {
        onTaskDataUpdate(newTaskData);
      }
    } catch (error) {
      console.error("Failed to update task level (Хочу легче):", error);
    } finally {
      setLoadingEasier(false);
    }
  };

  const handleMoreDifficultClick = async () => {
    if (!taskData) return;

    setLoadingMoreDifficult(true);
    const payload = {
      topic: taskData.topic,
      task_number: taskData.task_number,
      direction_of_complexity: "more_difficult",
      user_id: user_id,
      course_name: course_name,
    };

    try {
      const response = await fetch("api/tasks/level", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const newTaskData = await response.json();
      if (onTaskDataUpdate) {
        onTaskDataUpdate(newTaskData);
      }
    } catch (error) {
      console.error("Failed to update task level (Хочу сложнее):", error);
    } finally {
      setLoadingMoreDifficult(false);
    }
  };

  if (!user_id) {
    return
  }
  return (
    <section className="task-container">
      <div className="task-header">
        {!hideTopicName && (
          <h3 className="topic-title">
            Тема: <span>#{topicFront || "Не выбрана"}</span>
          </h3>
        )}
        {!hideThemeSelector && <ThemeSelector
          onSelectTheme={onSelectTheme}
          course_name={course_name}
          user_id={user_id}
        />}
      </div>

      <div className="task-frame">
        {loading ? (
          <div className="loading">Загрузка...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : taskData ? (
          <>
            <h2 className="task-title">{taskData.title}</h2>
            <div>
              <MarkdownParagraph markdownText={taskData.description} />
            </div>
            {taskData.code && (
              <div className="code-frame">
                <SyntaxHighlighter
                  language="python"
                  style={irBlack}
                  wrapLongLines
                  customStyle={{ padding: "1px" }}
                >
                  {taskData.code}
                </SyntaxHighlighter>
              </div>
            )}
          </>
        ) : (
          <div className="no-task">Выберите тему для отображения задачи</div>
        )}
      </div>

      <div className="feedback-menu">
        {showComplexityButtons && (
          <>
            <button
              className="easier-button"
              onClick={handleEasierClick}
              disabled={loadingEasier}
            >
              {loadingEasier ? "Загрузка..." : "Хочу легче"}
            </button>
            <button
              className="harder-button"
              onClick={handleMoreDifficultClick}
              disabled={loadingMoreDifficult}
            >
              {loadingMoreDifficult ? "Загрузка..." : "Хочу сложнее"}
            </button>
          </>
        )}
        <button
          className={`comment-button ${
            showCommentBox ? "comment-button-open" : ""
          }`}
          onClick={toggleCommentBox}
        >
          {showCommentBox ? "Закрыть" : "Оставить комментарий"}
        </button>
      </div>

      {showCommentBox && (
        <div className="comment-box">
          {commentSent ? (
            <h2>Спасибо за уделенное время</h2>
          ) : (
            <>
              <textarea
                className="comment-textarea"
                placeholder="Ваш комментарий..."
                rows={5}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <button
                className="send-comment-button"
                onClick={handleSendComment}
              >
                Отправить
              </button>
            </>
          )}
        </div>
      )}
    </section>
  );
}

export default Task;
